// Unified FP&A xlsx flow — drop one xlsx, run Snowflake load + Axial upload.
//
// Talks to /hd-api/forecast-xlsx/{preview,load,load/:jobId}. See FpaXlsxPanel
// in app/src/views/ForecastUpload/ and forecast-xlsx.js on the server.

import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, Alert, Chip, LinearProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, Switch, FormControlLabel, Tooltip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import StorageIcon from '@mui/icons-material/Storage';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MAX_FILE_SIZE = 25 * 1024 * 1024;

function fmtNum(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function fmtCurrency(val) {
  const n = Number(val);
  if (isNaN(n)) return val;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Power BI pulls from Snowflake on this fixed schedule.
const POWER_BI_REFRESH_TIMES = ['2:35 AM', '4:35 AM', '5:35 AM', '6:35 AM', '8:35 AM', '1:35 PM', '5:35 PM'];
const POWER_BI_REFRESH_MINUTES = [155, 275, 335, 395, 515, 815, 1055];

function nextPowerBiRefresh(now = new Date()) {
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const upcoming = POWER_BI_REFRESH_MINUTES.find(m => m > nowMins);
  const idx = upcoming !== undefined
    ? POWER_BI_REFRESH_MINUTES.indexOf(upcoming)
    : 0;
  return `${POWER_BI_REFRESH_TIMES[idx]}${upcoming === undefined ? ' tomorrow' : ''}`;
}

// ── Preview cards ─────────────────────────────────────────────────────────────

function DestinationStatsBlock({ preview, runSf, runAxial, dryRun }) {
  const { snowflake, axial } = preview;
  return (
    <Box>
      <Box sx={{ opacity: runSf ? 1 : 0.55 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <StorageIcon fontSize="small" color="action" />
          <Typography variant="overline" color="text.secondary">Power BI</Typography>
          {dryRun && <Chip icon={<ScienceIcon />} label="dry run" size="small" color="info" sx={{ height: 20, fontSize: 10 }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
          <Stat
            label="Will delete"
            value={dryRun ? '—' : (snowflake.willDeleteExistingRows === null ? '—' : fmtNum(snowflake.willDeleteExistingRows))}
            tone={!dryRun && snowflake.willDeleteExistingRows > 0 ? 'warning' : 'default'}
          />
          <Stat label="Will insert" value={fmtNum(snowflake.willInsertRows)} tone="success" />
        </Box>
      </Box>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 2 }} />

      <Box sx={{ opacity: runAxial && !dryRun ? 1 : 0.55 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CloudSyncIcon fontSize="small" color="action" />
          <Typography variant="overline" color="text.secondary">Axial</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
          <Stat label="Eligible" value={fmtNum(axial.eligibleRows)} tone="success" />
          <Stat label="Skipped" value={fmtNum(axial.skippedRows)} tone={axial.skippedRows > 0 ? 'warning' : 'default'} />
        </Box>
      </Box>
    </Box>
  );
}

function Stat({ label, value, tone = 'default' }) {
  const color = tone === 'success' ? 'success.main' : tone === 'warning' ? 'warning.main' : 'text.primary';
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color, fontWeight: 600, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  );
}

// ── Issues panel ──────────────────────────────────────────────────────────────

function IssuesPanel({ preview }) {
  const { axial, summary } = preview;
  const items = [
    { key: 'parseErrors', label: 'Parse errors', values: summary.parseErrors, severity: 'error' },
    { key: 'ambiguous', label: 'Ambiguous Axial company match', values: axial.ambiguousStores, severity: 'warning' },
    { key: 'noAxial', label: 'No matching Axial company', values: axial.noAxialStores, severity: 'warning' },
  ].filter(i => i.values && i.values.length);

  if (!items.length) return null;

  return (
    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map(item => (
        <Alert key={item.key} severity={item.severity}>
          <strong>{item.label}:</strong>{' '}
          {Array.isArray(item.values[0]) || typeof item.values[0] === 'string' || typeof item.values[0] === 'number'
            ? item.values.slice(0, 20).join(', ') + (item.values.length > 20 ? `, +${item.values.length - 20} more` : '')
            : `${item.values.length} issue(s)`}
        </Alert>
      ))}
    </Box>
  );
}

// ── Store rollup table ────────────────────────────────────────────────────────

function StoreRollupTable({ rollup }) {
  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Per-store roll-up ({rollup.length} stores)
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Store</TableCell>
              <TableCell>Name (DIM_STORE)</TableCell>
              <TableCell>Axial company</TableCell>
              <TableCell sx={{ width: 80 }}>Rows</TableCell>
              <TableCell sx={{ width: 140 }}>Axial status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rollup.map(r => (
              <TableRow key={r.storeId}>
                <TableCell>{r.storeId}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.storeName || '—'}</TableCell>
                <TableCell sx={{ fontSize: 13, color: r.axialName ? 'text.primary' : 'text.disabled' }}>
                  {r.axialName || '—'}
                </TableCell>
                <TableCell>{r.rowCount}</TableCell>
                <TableCell>
                  {r.eligibleForAxial ? (
                    <Chip icon={<CheckCircleIcon />} label="eligible" size="small" color="success" sx={{ height: 22 }} />
                  ) : (
                    <Tooltip title={r.skipReason || ''}>
                      <Chip icon={<WarningAmberIcon />} label="skipped" size="small" color="warning" sx={{ height: 22 }} />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ── Live progress strips ──────────────────────────────────────────────────────

function ProgressStrip({ title, icon: Icon, status, primary, secondary, color = 'primary' }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Icon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{title}</Typography>
        <StatusChip status={status} />
      </Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>{primary}</Typography>
      {secondary !== undefined && (
        <LinearProgress
          variant={typeof secondary === 'number' ? 'determinate' : 'indeterminate'}
          value={typeof secondary === 'number' ? secondary : undefined}
          color={color}
          sx={{ height: 4, borderRadius: 1 }}
        />
      )}
    </Paper>
  );
}

function StatusChip({ status }) {
  const map = {
    idle: { color: 'default', label: 'idle' },
    skipped: { color: 'default', label: 'skipped' },
    running: { color: 'info', label: 'running' },
    done: { color: 'success', label: 'done' },
    error: { color: 'error', label: 'error' },
    cancelled: { color: 'warning', label: 'cancelled' },
  };
  const it = map[status] || map.idle;
  return <Chip size="small" color={it.color} label={it.label} sx={{ height: 20, fontSize: 10 }} />;
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function FpaXlsxPanel({ getToken }) {
  const abortRef = useRef(null);

  const [phase, setPhase] = useState('idle'); // idle | previewing | preview | loading | done
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [version, setVersion] = useState('');
  const [runSf, setRunSf] = useState(true);
  const [runAxial, setRunAxial] = useState(true);
  const [dryRun, setDryRun] = useState(false);

  const [sfState, setSfState] = useState({ status: 'idle', phase: '', deleted: 0, inserted: 0, totalToInsert: 0, error: null });
  const [axialState, setAxialState] = useState({
    started: false, total: 0, completed: 0,
    created: 0, updated: 0, failed: 0, locked: 0,
    lastError: null, fatalError: null,
  });
  const [jobId, setJobId] = useState(null);
  const [finalSummary, setFinalSummary] = useState(null);

  // ── Drop / preview ──────────────────────────────────────────────────────────

  const onDrop = useCallback(async (accepted, rejected) => {
    if (rejected.length) {
      setError(rejected[0].errors?.[0]?.message || 'Invalid file.');
      return;
    }
    const file = accepted[0];
    if (file.size > MAX_FILE_SIZE) { setError('File is too large (max 25 MB).'); return; }

    setError(null);
    setPreview(null);
    setFinalSummary(null);
    setPhase('previewing');

    try {
      const token = await getToken();
      const form = new FormData();
      form.append('xlsx', file);
      const { data } = await axios.post('/hd-api/forecast-xlsx/preview', form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(data);
      setVersion(data.version || '');
      setPhase('preview');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Preview failed.');
      setPhase('idle');
    }
  }, [getToken]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls', '.xlsx'],
    },
    maxFiles: 1,
  });

  // ── Load ────────────────────────────────────────────────────────────────────

  async function handleRun() {
    if (!preview?.previewId) return;
    if (!version.trim()) { setError('VERSION is required.'); return; }

    setError(null);
    setSfState({ status: runSf ? 'running' : 'skipped', phase: '', deleted: 0, inserted: 0, totalToInsert: 0, error: null });
    setAxialState({
      started: false,
      total: preview.axial.eligibleRows,
      completed: 0,
      created: 0, updated: 0, failed: 0, locked: 0,
      lastError: null, fatalError: null,
    });
    setFinalSummary(null);
    setPhase('loading');

    const token = await getToken();

    try {
      const response = await fetch('/hd-api/forecast-xlsx/load', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          previewId: preview.previewId,
          version: version.trim(),
          runSnowflake: runSf,
          runAxial,
          dryRun,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${response.status}`);
      }

      const xJobId = response.headers.get('X-Job-Id');
      if (xJobId) setJobId(xJobId);

      const reader = response.body.getReader();
      abortRef.current = { reader };
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }
          handleSseEvent(event);
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(e.message || 'Load failed.');
      }
    } finally {
      abortRef.current = null;
      setJobId(null);
      setPhase('done');
    }
  }

  function handleSseEvent(event) {
    if (event.type === 'snowflake') {
      setSfState(prev => ({
        ...prev,
        status: event.status || prev.status,
        phase: event.phase ?? prev.phase,
        deleted: event.deleted ?? prev.deleted,
        inserted: event.inserted ?? prev.inserted,
        totalToInsert: event.totalToInsert ?? prev.totalToInsert,
        error: event.error ?? prev.error,
      }));
    } else if (event.type === 'row' && event.half === 'axial') {
      setAxialState(prev => {
        if (event.status === 'fatal') return { ...prev, fatalError: event.message };
        const next = { ...prev, started: true };
        if (event.status === 'ok') {
          next.completed = prev.completed + 1;
          if (event.operation === 'created') next.created = prev.created + 1;
          else next.updated = prev.updated + 1;
        } else if (event.status === 'error') {
          next.completed = prev.completed + 1;
          next.failed = prev.failed + 1;
          next.lastError = event.message;
        } else if (event.status === 'locked') {
          next.completed = prev.completed + 1;
          next.locked = prev.locked + 1;
        } else if (event.status === 'cancelled') {
          // counted as not-completed; status will end at 'cancelled'.
        }
        return next;
      });
    } else if (event.type === 'axial' && event.status === 'skipped') {
      setAxialState(prev => ({ ...prev, started: false }));
    } else if (event.type === 'done') {
      setFinalSummary(event);
    }
  }

  async function handleCancel() {
    if (jobId) {
      const token = await getToken();
      if (token) {
        axios.delete(`/hd-api/forecast-xlsx/load/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    }
    abortRef.current?.reader?.cancel();
  }

  function handleReset() {
    setPreview(null);
    setVersion('');
    setPhase('idle');
    setError(null);
    setFinalSummary(null);
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const isLoading = phase === 'loading';
  const axialEffective = runAxial && !dryRun;
  const canRun = phase === 'preview' && version.trim() && (runSf || axialEffective);

  return (
    <Box>
      {/* Drop zone */}
      {phase === 'idle' || phase === 'previewing' ? (
        <Paper
          {...getRootProps()}
          variant="outlined"
          sx={{
            p: 4, mb: 2, textAlign: 'center', cursor: phase === 'previewing' ? 'wait' : 'pointer',
            borderStyle: 'dashed',
            borderColor: isDragActive ? 'secondary.main' : 'divider',
            bgcolor: isDragActive ? (t) => alpha(t.palette.secondary.main, 0.04) : 'background.paper',
          }}
        >
          <input {...getInputProps()} disabled={phase === 'previewing'} />
          {phase === 'previewing' ? (
            <Box>
              <CircularProgress size={28} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Parsing xlsx…</Typography>
            </Box>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                {isDragActive ? 'Drop the FP&A xlsx here' : 'Drop the FP&A forecast xlsx here, or click to browse'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sheet: "Daily Forecast" · One file feeds both Power BI load + Axial upload · Max 25 MB
              </Typography>
            </>
          )}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>{preview?.filename}</Typography>
          <Button size="small" onClick={handleReset} disabled={isLoading}>Replace file</Button>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {preview && (
        <>
          {dryRun && (
            <Alert severity="info" icon={<ScienceIcon />} sx={{ mb: 2 }}>
              <strong>Dry run:</strong> Power BI writes go to a test destination — production data is untouched, and the Axial upload is skipped.
            </Alert>
          )}

          {/* Preview stats + mode selectors + Run — one row on wide screens, wraps on narrow */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}>
              <TextField
                label="VERSION"
                value={version}
                onChange={e => setVersion(e.target.value)}
                size="small"
                sx={{ minWidth: 240, alignSelf: 'center', '& .MuiFormHelperText-root': { m: 0, mt: 0.5 } }}
                helperText={preview.version && preview.version !== version ? `inferred: ${preview.version}` : ''}
              />
              <Stat label="Rows" value={fmtNum(preview.summary.rows)} />
              <Stat label="Stores" value={fmtNum(preview.summary.stores)} />
              {preview.summary.dateRange && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Date range</Typography>
                  <Typography sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 14, lineHeight: 1.1, mt: 0.4 }}>
                    {preview.summary.dateRange.from} → {preview.summary.dateRange.to}
                  </Typography>
                </Box>
              )}
              <Box sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 1,
                ml: { xs: 0, lg: 'auto' },
              }}>
                <FormControlLabel
                  control={<Switch checked={dryRun} onChange={e => setDryRun(e.target.checked)} disabled={isLoading} color="info" />}
                  label="Dry run"
                />
                <FormControlLabel
                  control={<Switch checked={runSf} onChange={e => setRunSf(e.target.checked)} disabled={isLoading} />}
                  label="Run Power BI load"
                />
                <FormControlLabel
                  control={<Switch checked={runAxial && !dryRun} onChange={e => setRunAxial(e.target.checked)} disabled={isLoading || dryRun} />}
                  label={dryRun ? 'Run Axial upload (disabled in dry run)' : 'Run Axial upload'}
                />
                {isLoading ? (
                  <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
                    Cancel
                  </Button>
                ) : (
                  <Tooltip title={!version.trim() ? 'Set VERSION first' : !canRun ? 'Enable at least one half' : ''}>
                    <span>
                      <Button variant="contained" onClick={handleRun} disabled={!canRun}>
                        Run
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Paper>

          {phase === 'preview' && preview.axial.unmatchedStores && preview.axial.unmatchedStores.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Stores not found in database:</strong>{' '}
              {preview.axial.unmatchedStores.slice(0, 20).join(', ')}
              {preview.axial.unmatchedStores.length > 20 ? `, +${preview.axial.unmatchedStores.length - 20} more` : ''}
            </Alert>
          )}

          <IssuesPanel preview={preview} />

          {/* Live progress — visible only while the run is in flight */}
          {phase === 'loading' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Running…</Typography>

              <ProgressStrip
                title="Power BI"
                icon={StorageIcon}
                status={sfState.status}
                primary={
                  sfState.status === 'skipped'
                    ? 'skipped'
                    : sfState.status === 'error'
                    ? `Failed: ${sfState.error || 'unknown error'}`
                    : sfState.phase === 'delete'
                    ? 'Deleting existing rows…'
                    : sfState.phase === 'insert'
                    ? `Inserted ${fmtNum(sfState.inserted)} / ${fmtNum(sfState.totalToInsert)} (deleted ${fmtNum(sfState.deleted)})`
                    : sfState.status === 'done'
                    ? `Done — deleted ${fmtNum(sfState.deleted)}, inserted ${fmtNum(sfState.inserted)}`
                    : 'Waiting…'
                }
                secondary={
                  sfState.status === 'done' ? 100
                  : sfState.phase === 'insert' && sfState.totalToInsert
                  ? Math.round((sfState.inserted / sfState.totalToInsert) * 100)
                  : 'indeterminate'
                }
                color={sfState.status === 'error' ? 'error' : 'primary'}
              />

              <ProgressStrip
                title="Axial"
                icon={CloudSyncIcon}
                status={
                  !axialEffective ? 'skipped'
                  : axialState.fatalError ? 'error'
                  : axialState.started ? 'running'
                  : 'idle'
                }
                primary={
                  !axialEffective
                    ? (dryRun ? 'skipped (dry run)' : 'skipped')
                    : axialState.fatalError
                    ? `Failed: ${axialState.fatalError}`
                    : `${fmtNum(axialState.created)} created · ${fmtNum(axialState.updated)} updated · ${fmtNum(axialState.failed)} failed${axialState.locked > 0 ? ` · ${fmtNum(axialState.locked)} locked` : ''}`
                      + ` — ${fmtNum(axialState.completed)} / ${fmtNum(axialState.total)} rows`
                }
                secondary={
                  !axialEffective ? undefined
                  : axialState.total
                  ? Math.round((axialState.completed / axialState.total) * 100)
                  : 'indeterminate'
                }
                color={axialState.failed > 0 ? 'warning' : 'primary'}
              />

              {axialState.lastError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: -0.5, mb: 1, pl: 1 }}>
                  Last error: {axialState.lastError}
                </Typography>
              )}
            </Box>
          )}

          {/* Done — compact summary line only */}
          {phase === 'done' && finalSummary && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ mr: 1 }}>Load results:</Typography>
                <Chip
                  label={`status: ${finalSummary.status}`}
                  color={finalSummary.status === 'complete' ? 'success' : finalSummary.status === 'cancelled' ? 'warning' : 'error'}
                  size="small"
                />
                {finalSummary.snowflake?.inserted !== undefined && (
                  <Chip icon={<StorageIcon />} label={`Power BI: ${fmtNum(finalSummary.snowflake.deleted)} del / ${fmtNum(finalSummary.snowflake.inserted)} ins`} size="small" />
                )}
                {finalSummary.axial && (
                  <>
                    <Chip icon={<CheckCircleIcon />} label={`${fmtNum(finalSummary.axial.created)} created`} color="success" size="small" />
                    <Chip icon={<CheckCircleIcon />} label={`${fmtNum(finalSummary.axial.updated)} updated`} color="primary" size="small" />
                    {finalSummary.axial.failed > 0 && (
                      <Chip icon={<ErrorIcon />} label={`${fmtNum(finalSummary.axial.failed)} failed`} color="error" size="small" />
                    )}
                  </>
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={handleReset}>Upload another</Button>
              </Box>
              {finalSummary.snowflake?.inserted > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Power BI refreshes from Snowflake at {POWER_BI_REFRESH_TIMES.join(', ')}. Next refresh: <strong>{nextPowerBiRefresh()}</strong>.
                </Typography>
              )}
            </Paper>
          )}

          {/* Technical details — closed by default; opened for troubleshooting */}
          <Accordion variant="outlined" disableGutters sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={600}>Show technical details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <DestinationStatsBlock
                  preview={preview}
                  runSf={runSf}
                  runAxial={runAxial}
                  dryRun={dryRun}
                />
              </Box>
              {phase === 'done' && (
                <Box sx={{ p: 2, pb: 0 }}>
                  <ProgressStrip
                    title="Snowflake"
                    icon={StorageIcon}
                    status={sfState.status}
                    primary={
                      sfState.status === 'skipped'
                        ? 'skipped'
                        : sfState.status === 'error'
                        ? `Failed: ${sfState.error || 'unknown error'}`
                        : `Done — deleted ${fmtNum(sfState.deleted)}, inserted ${fmtNum(sfState.inserted)}`
                    }
                    secondary={sfState.status === 'done' ? 100 : undefined}
                    color={sfState.status === 'error' ? 'error' : 'primary'}
                  />
                  <ProgressStrip
                    title="Axial"
                    icon={CloudSyncIcon}
                    status={
                      !axialEffective ? 'skipped'
                      : axialState.fatalError ? 'error'
                      : finalSummary?.status === 'cancelled' ? 'cancelled'
                      : 'done'
                    }
                    primary={
                      !axialEffective
                        ? (dryRun ? 'skipped (dry run)' : 'skipped')
                        : axialState.fatalError
                        ? `Failed: ${axialState.fatalError}`
                        : `${fmtNum(axialState.created)} created · ${fmtNum(axialState.updated)} updated · ${fmtNum(axialState.failed)} failed`
                          + ` — ${fmtNum(axialState.completed)} / ${fmtNum(axialState.total)} rows`
                    }
                    secondary={!axialEffective ? undefined : 100}
                    color={axialState.failed > 0 ? 'warning' : 'primary'}
                  />
                </Box>
              )}
              <Box sx={{ p: 2 }}>
                <StoreRollupTable rollup={preview.storeRollup} />
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}
